import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { icons } from "@/components/Base/Lucide";

export interface Menu {
    icon: keyof typeof icons;
    title: string;
    badge?: number;
    pathname?: string;
    subMenu?: Menu[];
    ignore?: boolean;
}

export interface SideMenuState {
    menu: Array<Menu | string>;
    selectedUserId?: string;
    projectName?: string;
}

const initialState: SideMenuState = {
    menu: [
        "DASHBOARDS",
        {
            icon: "TrendingUp",
            pathname: "/dashboard",
            title: "Sales",
        },
        {
            icon: "CircleDollarSign",
            pathname: "/pricing",
            title: "Pricing",
        },
        {
            icon: "Layers",
            pathname: "/stock",
            title: "Stock",
        },

        "DATA",
        {
            icon: "Database",
            pathname: "/products",
            title: "Master Data",
        },

        "MANAGE",
        {
            icon: "Package",
            pathname: "/products",
            title: "Product",
        },
        {
            icon: "Users",
            pathname: "/users",
            title: "User",
        },
        {
            icon: "Users",
            pathname: "/teams",
            title: "Team",
        },
        {
            icon: "Store",
            pathname: "/outlets",
            title: "Outlet",
        },

        "STOCK",
        {
            icon: "FileBarChart",
            pathname: "/reports",
            title: "OOS Threshold",
        },
        {
            icon: "Layers",
            pathname: "/stock-batch",
            title: "Stock Batch",
            ignore: true,
        },

        "ATTENDANCE & WORKPLANNER",
        {
            icon: "CalendarCheck",
            pathname: "/attendance",
            title: "Attendance Info",
        },
        {
            icon: "ClipboardCheck",
            pathname: "/workplanner-status",
            title: "Workplanner Status",
            ignore: true,
        },

        "MERCHANDISER",
        {
            icon: "Package",
            pathname: "/merchandiser-products",
            title: "Products",
            ignore: true,
        },
        {
            icon: "ShoppingCart",
            pathname: "/merchandising",
            title: "Merchandising",
            ignore: true,
        },

        "MARKET INTELLIGENCE",
        {
            icon: "ClipboardList",
            pathname: "/survey",
            title: "Survey",
            ignore: true,
        },
        "PROJECT SETTINGS",
        {
            icon: "Settings",
            pathname: "/project-settings",
            title: "Settings",
            ignore: true,
        },
    ],
    selectedUserId: undefined,
    projectName: undefined,
};

export const sideProjectMenuSlice = createSlice({
    name: "sideProjectMenu",
    initialState,
    reducers: {
        setCompanyId: (state, action) => {
            const companyId = action.payload;

            // Update the pathname for company-specific items
            state.menu = state.menu.map((item) => {
                if (typeof item !== "string") {
                    if (item.title === "User") {
                        return { ...item, pathname: `/users?companyId=${companyId}` };
                    }
                    if (item.title === "Team") {
                        return { ...item, pathname: `/teams?companyId=${companyId}` };
                    }
                }
                return item;
            });
        },
        setUserId: (state, action) => {
            state.selectedUserId = action.payload;
        },
        setProjectId: (state, action) => {
            const projectId = action.payload;

            // Update the pathname for project-specific items
            state.menu = state.menu.map((item) => {
                if (typeof item !== "string") {
                    // Update Sales menu item to link to project dashboard
                    if (item.title === "Sales") {
                        return { ...item, pathname: `/projects/${projectId}/dashboard` };
                    }
                    if (item.title === "Pricing") {
                        return { ...item, pathname: `/projects/${projectId}/pricing` };
                    }
                    // Update main menu items to include projectId
                    if (item.title === "Product") {
                        return { ...item, pathname: `/products?projectId=${projectId}` };
                    }
                    if (item.title === "User") {
                        return { ...item, pathname: `/users?projectId=${projectId}` };
                    }
                    if (item.title === "Outlet") {
                        return { ...item, pathname: `/outlets?projectId=${projectId}` };
                    }
                    if (item.title === "Team") {
                        return { ...item, pathname: `/teams?projectId=${projectId}` };
                    }
                    if (item.title === "Attendance Info") {
                        return { ...item, pathname: `/projects/${projectId}/attendance` };
                    }
                    if (item.title === "Workplanner Status") {
                        return { ...item, pathname: `/projects/${projectId}/workplanner-status` };
                    }
                    if (item.title === "Products") {
                        return { ...item, pathname: `/projects/${projectId}/merchandiser-products` };
                    }
                    if (item.title === "Merchandising") {
                        return { ...item, pathname: `/projects/${projectId}/merchandising` };
                    }
                    if (item.title === "OOS Threshold") {
                        return { ...item, pathname: `/projects/${projectId}/reports` };
                    }
                    if (item.title === "Stock") {
                        return { ...item, pathname: `/projects/${projectId}/stock` };
                    }
                    if (item.title === "Stock Batch") {
                        return { ...item, pathname: `/projects/${projectId}/stock-batch` };
                    }
                    if (item.title === "Survey") {
                        return { ...item, pathname: `/projects/${projectId}/survey` };
                    }
                    if (item.title === "Settings") {
                        return { ...item, pathname: `/projects/${projectId}/project-settings` };
                    }
                    // Update Master Data menu item to link to master-data page
                    if (item.title === "Master Data" || item.title.endsWith(" - Master Data")) {
                        return { ...item, pathname: `/projects/${projectId}/master-data` };
                    }
                    // Update submenu items if needed
                    if (item.subMenu) {
                        item.subMenu = item.subMenu.map(subItem => {
                            if (subItem.title === "Add Team") {
                                return { ...subItem, pathname: `/teams/add?projectId=${projectId}` };
                            }
                            if (subItem.title === "Add Team Route") {
                                return { ...subItem, pathname: `/teams/route?projectId=${projectId}` };
                            }
                            return subItem;
                        });
                    }
                }
                return item;
            });
        },
        setProjectName: (state, action) => {
            const projectName = action.payload;
            state.projectName = projectName;

            // Update the "Master Data" menu item title to include project name
            state.menu = state.menu.map((item) => {
                if (typeof item !== "string") {
                    // Check if title is "Master Data" or ends with " - Master Data"
                    const isMasterData = item.title === "Master Data" || item.title.endsWith(" - Master Data");
                    if (isMasterData) {
                        return { 
                            ...item, 
                            title: projectName ? `${projectName} - Master Data` : "Master Data"
                        };
                    }
                }
                return item;
            });
        },
        resetProjectMenu: (state) => {
            // Reset menu to initial state (without project context)
            state.menu = initialState.menu;
            state.projectName = undefined;
        }
    },
});

export const { setCompanyId, setUserId, setProjectId, setProjectName, resetProjectMenu } = sideProjectMenuSlice.actions;

export const selectSideProjectMenu = (state: RootState) => state.sideProjectMenu.menu;

export default sideProjectMenuSlice.reducer;

